import { UIElement } from '../ui-element.js';
import { BrandColors } from '../../global/helpers-marketing.js';
import styles from './ui-badge.css';
import { hasValue } from '../../global/helpers.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIBadge
 * @element ui-badge
 * @classdesc Represents a class for <code>ui-badge</code> element.
 * Small badge in the corner for advertising purposes.
 * @property {UIBadgeLayout} [layout] {@attr layout} Shape of the badge.
 * @property {UIBrandColor | "yellow"} [color] {@attr color} Color of the badge.
 * @slot
 * @example
 * <ui-badge>Badge</ui-badge>
 * <ui-badge layout="pin" color="orange">1</ui-badge>
 * <ui-badge color="orange" layout="pin"><ui-icon glyph="redesign-heart"></ui-icon></ui-badge>
 * <ui-badge color="yellow" layout="segment">Gold customer</ui-badge>
 * <ui-badge color="lilac" layout="segment-small">Gold customer</ui-badge>
 */
class UIBadge extends UIElement {
    /**
     * @type {UIBadgeLayouts}
     */
    static get Layout() {
        return {
            Default: 'default',
            Pin: 'pin',
            Segment: 'segment',
            SegmentSmall: 'segment-small',
        };
    }

    /**
     * @param {UIElement} component
     * @param {string} value
     * @private
     */
    static validateColor(component, value) {
        const allowedOptions =
            'Allowed options are: "orange", "yellow", "lilac", "turquoise".';

        if (['blue', 'pineapple', 'bark'].includes(value)) {
            console.warn(
                `${value} - color is deprecated. ${allowedOptions}`,
                component
            );
            return;
        }

        if (!hasValue({ ...BrandColors, Yellow: 'yellow' }, value)) {
            console.warn(
                `${value} - value does not exist. ${allowedOptions}`,
                component
            );
        }
    }

    /**
     * @param {UIElement} component
     * @param {string} value
     * @private
     */
    static validateLayout(component, value) {
        if (!hasValue(UIBadge.Layout, value)) {
            console.warn(
                `${value} - value does not exist. Allowed options are: "pin" and "segment".`,
                component
            );
        }
    }

    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                color: {
                    type: String,
                    default: BrandColors.Turquoise,
                    validate:
                        /** @type {IAttributeValidationHandler} */ this
                            .validateColor,
                },
                layout: {
                    type: String,
                    default: UIBadge.Layout.Default,
                    validate:
                        /** @type {IAttributeValidationHandler} */ this
                            .validateLayout,
                },
            },
        };
    }

    /**
     * @private
     * @returns {boolean}
     */
    hasFixedWidth() {
        if (this.layout !== UIBadge.Layout.Pin) {
            return false;
        }

        let hasElementNode = false;
        let hasLongTextNode = false;
        let hasShortTextNode = false;

        [].forEach.call(this.childNodes, (node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                hasElementNode = true;
                return;
            }

            if (node.nodeType === Node.TEXT_NODE) {
                const nodeTextValue = node.textContent.trim();

                if (nodeTextValue.length === 0) {
                    return;
                }

                if (nodeTextValue.length > 2) {
                    hasLongTextNode = true;
                } else {
                    hasShortTextNode = true;
                }
            }
        });

        return !hasLongTextNode && (!hasElementNode || !hasShortTextNode);
    }

    /**
     * @inheritDoc
     */
    render() {
        this.hasFixedWidth() && this.classList.add('ui-badge__fixed-width');
    }
}

UIBadge.defineElement('ui-badge', styles);
export { UIBadge };
